import React, { useMemo } from "react";
import { Slider } from "primereact/slider";
import { ScenarioCpvItem } from "../../../stores/scenarioPlanningStore";
import "./ScenarioLine.css";

export type ScenarioLineProps = {
	scenarios: ScenarioCpvItem[];
	className?: string;
};

const ScenarioLine: React.FC<ScenarioLineProps> = ({
	scenarios,
	className,
}) => {
	// Process scenarios to find extremes and middle scenarios
	const { asIs, cherryPicking, otherScenarios, min, max, currency, unit } = useMemo(() => {
		if (!scenarios || scenarios.length === 0) {
			return {
				asIs: null,
				cherryPicking: null,
				otherScenarios: [],
				min: 0,
				max: 100,
				currency: "€",
				unit: "m",
			};
		}

		// Find As-Is (should have highest CPV) and Cherry Picking (lowest CPV)
		const asIsScenario = scenarios.find(
			(s) => s.scenario_name.toLowerCase().includes("as-is") || s.scenario_name.toLowerCase().includes("as is")
		);
		const cherryPickingScenario = scenarios.find(
			(s) => s.scenario_name.toLowerCase().includes("cherry picking")
		);

		// All other scenarios go in the middle
		const others = scenarios.filter(
			(s) => s !== asIsScenario && s !== cherryPickingScenario
		);

		// Calculate min/max from all scenarios
		const allValues = scenarios.map((s) => s.total_target_cpv);
		const minVal = Math.min(...allValues);
		const maxVal = Math.max(...allValues);

		// Get currency symbol from API response
		const curr = scenarios[0]?.currency || "EUR";
		const currencySymbol = curr === "EUR" ? "€" : curr === "USD" ? "$" : curr === "GBP" ? "£" : curr;

		return {
			asIs: asIsScenario,
			cherryPicking: cherryPickingScenario,
			otherScenarios: others,
			min: minVal,
			max: maxVal,
			currency: currencySymbol,
			unit: "m",
		};
	}, [scenarios]);

	const calculatePosition = (value: number) => {
		const denom = max - min || 1;
		return ((value - min) / denom) * 100;
	};

	const fmt = (n: number, scenarioName?: string) => {
		// Convert to millions
		const valueInMillions = n / 1000000;
		const formatted = `${valueInMillions.toFixed(2)}${unit}`;

		if (scenarioName) {
			return `${formatted} (${currency}) (${scenarioName})`;
		}
		return `${currency} ${formatted}`;
	};

	if (!scenarios || scenarios.length === 0) {
		return (
			<div className="scenario-line">
				<div className="sl-container">
					<p className="text-center text-gray-500">No scenario data available</p>
				</div>
			</div>
		);
	}

	return (
		<div className={["scenario-line", className].filter(Boolean).join(" ")}>
			<div className="sl-container">
				<div className="sl-header">
					<span className="sl-title">
						<span className="sl-title-bold">Incumbent CPV</span>{" "}
						<br />
						<span className="sl-title-sub"> (As-Is Scenario)</span>
					</span>
					<span className="sl-title">
						<span className="sl-title-bold">Best Case</span> <br />
						<span className="sl-title-sub">
							(Cherry Picking Scenario)
						</span>
					</span>
				</div>

				<div className="sl-track-wrap">
					{/* Slider used as a visual baseline */}
					<Slider
						value={min}
						min={min}
						max={max}
						disabled
						className="sl-slider"
					/>

					{/* Render markers for all middle scenarios */}
					{otherScenarios.map((scenario, idx) => {
						const pct = calculatePosition(scenario.total_target_cpv);
						const isRecommended = scenario.is_recommended === true;
						return (
							<div
								key={idx}
								className={`sl-marker ${isRecommended ? 'sl-marker-recommended' : 'sl-marker-default'}`}
								style={{ right: `calc(${pct}% - 8px)` }}
								aria-label={`${scenario.scenario_name} ${fmt(scenario.total_target_cpv, scenario.scenario_name)}`}
								role="img"
							>
								<div className={`sl-marker-icon ${isRecommended ? '' : 'sl-marker-icon-default'}`} />
								<div className={`sl-marker-value ${isRecommended ? '' : 'sl-marker-value-default'}`}>
									{fmt(scenario.total_target_cpv, scenario.scenario_name)}
								</div>
							</div>
						);
					})}
				</div>

				{/* Min / Max labels */}
				<div className="sl-endpoints">
					<span className="sl-endpoint">
						{asIs ? fmt(asIs.total_target_cpv) : fmt(max)}
					</span>
					<span className="sl-endpoint">
						{cherryPicking ? fmt(cherryPicking.total_target_cpv) : fmt(min)}
					</span>
				</div>
			</div>
		</div>
	);
};

export default ScenarioLine;
